import React, { useState, useEffect } from 'react';
import { Container, TextField, Button, Grid, Paper, ToggleButton, ToggleButtonGroup, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism'; 
import './App.css';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#2F80FF',
    },
    text: {
      primary: '#FFFFFF',
    },
  },
  typography: {
    fontFamily: 'Montserrat, Arial, sans-serif',
    h5: {
      fontWeight: 700,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontFamily: 'Montserrat, Arial, sans-serif',
          fontSize: '16px',
          fontWeight: 700,
          borderRadius: '20px',
          position: 'relative',
          '&:hover': {
            backgroundColor: '#4175BD',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          borderRadius: '20px',
          '& .MuiOutlinedInput-root': {
            borderRadius: '20px',
            '& fieldset': {
              border: 'none',
            },
          },
          backgroundColor: '#535353',
        },
      },
    },
    // Styling for the Select component to match the TextField styling
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: '20px',
          backgroundColor: '#535353',
          '& .MuiOutlinedInput-root': {
            borderRadius: '20px',
            '& fieldset': {
              border: 'none',
            },
          },
          color: '#FFFFFF',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: '20px',
          border: 'none',
          '&.Mui-selected': {
            backgroundColor: '#2F80FF',
            color: '#FFF',
            '&:hover': {
              backgroundColor: '#4175BD',
            },
          },
          padding: '10px 20px',
          margin: '0 5px',
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        grouped: {
          borderRadius: '20px',
          '&:not(:last-child)': {
            borderRight: 'none',
          },
        },
      },
    },
  },
});

const customStyle = {
  ...vscDarkPlus,
  'pre[class*="language-"]': {
    ...vscDarkPlus['pre[class*="language-"]'],
    background: '#535353',
  },
  'code[class*="language-"]': {
    ...vscDarkPlus['code[class*="language-"]'],
    background: '#535353',
  },
};

const styles = {
  paper: {
    padding: '20px',
    marginTop: '20px',
    width: '100%',
    overflowX: 'auto',
    backgroundColor: '#535353',
    borderRadius: '40px',
  },
  buttonIcon: {
    position: 'absolute',
    right: '20px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '20px',
    height: 'auto',
  },
  buttonWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  boxView: {
    padding: '10px',
    margin: '10px 0',
    borderRadius: '20px',
    backgroundColor: '#535353',
    color: '#BABABA',
    textAlign: 'left',
  },
  twoColumnGrid: {
    padding: '10px',
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '15px',
    width: '100%',
  },
};

const countries = [
  { code: 'AT', name: 'Austria' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'HR', name: 'Croatia' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'DK', name: 'Denmark' },
  { code: 'EE', name: 'Estonia' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'EL', name: 'Greece' },
  { code: 'HU', name: 'Hungary' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IT', name: 'Italy' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MT', name: 'Malta' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'RO', name: 'Romania' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'ES', name: 'Spain' },
  { code: 'SE', name: 'Sweden' },
];

const JSONDisplay = ({ data }) => (
  <Paper elevation={0} style={styles.paper}>
    <SyntaxHighlighter language="json" style={customStyle}>
      {JSON.stringify(data, null, 2)}
    </SyntaxHighlighter>
  </Paper>
);

const fieldMapping = {
  navn: 'Navn',
  sammensatStatus: 'Status',
  postnummer: 'Postnummer',
  postdistrikt: 'By',
  vejnavn : 'Vejnavn',
  husnummerFra: 'Husnummer',
};

const FriendlyDisplay = ({ data }) => {
  const mappedFields = Object.keys(fieldMapping)
    .filter((key) => data[key] !== undefined) // Only show fields that exist in the data
    .map((key) => ({
      label: fieldMapping[key],
      value: data[key],
    }));

  return (
    <Grid container spacing={2} style={{ margin: '5px', marginLeft: '10px' }}>
      {mappedFields.map((field, index) => (
        <Grid item xs={12} sm={6} key={index}> {/* Two columns on larger screens */}
          <TextField
            fullWidth
            label={field.label}
            value={typeof field.value === 'object' ? JSON.stringify(field.value, null, 2) : field.value}
            variant="outlined"
            InputProps={{
              readOnly: true,
              style: { 
                pointerEvents: 'none',  // Prevents mouse interactions
                cursor: 'default',      // Changes cursor to default when hovering
              },
            }}
            style={{ backgroundColor: '#535353' }} // Keep consistent background color
          />
        </Grid>
      ))}
    </Grid>
  );
};


const App = () => {
  const [responseData, setResponseData] = useState(
    Object.keys(fieldMapping).reduce((acc, key) => {
      acc[key] = ''; // Initialize each field with an empty string
      return acc;
    }, {})
  );
  const [viewMode, setViewMode] = useState('friendly'); // Default view mode
  const [selectedCountry, setSelectedCountry] = useState('DK');
  const [query, setQuery] = useState('');
  const handleSubmit = async (event) => {
    event.preventDefault(); 
    if (!query || !selectedCountry) {
        console.error("Query or country cannot be empty.");
        return; // Prevent API call if inputs are invalid
    }
    try {
        // Construct the query parameters for the API URL
        const queryParams = new URLSearchParams({
            query,
            country: selectedCountry
        }).toString();

        const url = `${process.env.REACT_APP_API_URL}/fetchdata?${queryParams}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                "Authorization": `Bearer ${process.env.REACT_APP_VIES_API_KEY}`,
                'Content-Type': 'application/json',
            },
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        delete data.direktoer;
        delete data.bestyrelsesmedlemmer;
        delete data.finance;
        delete data.telefaxNummer;
        setResponseData(data); 
    } catch (error) {
        console.error('Error:', error);
    }
};

  const handleCountryChange = (event) => setSelectedCountry(event.target.value);
  const handleQueryChange = (event) => setQuery(event.target.value);
  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="sm">
        <Grid container spacing={2} alignItems="center" justifyContent="center" style={{ marginTop: '20px' }}>
          <Grid item xs={12}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2} alignItems="center" justifyContent="center">
              <Grid item xs={4}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="country-label">Land</InputLabel>
                    <Select
                      labelId="country-label"
                      id="country"
                      value={selectedCountry}
                      onChange={handleCountryChange}
                      label="Land"
                    >
                      {countries.map((country) => (
                        <MenuItem key={country.code} value={country.code}>
                          {country.name} ({country.code})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="VAT-nummer"
                    name="query"
                    variant="outlined"
                    value={query}
                    onChange={handleQueryChange}
                  />
                </Grid>
                <Grid item xs={8}>
                  <div style={styles.buttonWrapper}>
                    <Button type="submit" variant="contained" color="primary" fullWidth>
                      Hent data
                      <img src={`${process.env.PUBLIC_URL}/arrow.svg`} style={styles.buttonIcon} alt="Arrow icon" />
                    </Button>
                  </div>
                </Grid>
              </Grid>
            </form>
          </Grid>

          {responseData && (
            <Grid item xs={12}>
              <Grid container justifyContent="center">
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={handleViewModeChange}
                  aria-label="View mode"
                >
                  <ToggleButton value="friendly" aria-label="Show Friendly View">
                    Tabel
                  </ToggleButton>
                  <ToggleButton value="json" aria-label="Show JSON">
                    JSON
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            </Grid>
          )}

          {responseData && viewMode === 'json' && <JSONDisplay data={responseData} />}
          {responseData && viewMode === 'friendly' && <FriendlyDisplay data={responseData} />}
        </Grid>
      </Container>
    </ThemeProvider>
  );
};

export default App;
